import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export interface ProgressBarItemConfig {
	key?: string;
	totalKey?: string;
	value?: number;
	label?: string;
	colorClass?: string;
	textClass?: string;
	displayMode?: 'percentage' | 'value' | 'value_percentage';
}

export interface ProgressBarCellRendererParams {
	bars?: Array<ProgressBarItemConfig>;
	summaryKey?: string;
	totalLabel?: string;
}

export interface RenderedBarItem {
	percentage: number;
	displayText: string;
	colorClass: string;
	textClass: string;
}

@Component({
	selector: 'app-progress-bar-cell-renderer',
	template: `
		<div class="d-flex flex-column justify-content-center h-100 py-1" style="gap: 3px; min-width: 105px;" [title]="tooltip">
			@for (bar of renderedBars; track bar) {
				<div class="d-flex align-items-center" style="gap: 6px; font-size: 11px; line-height: 1.1;">
					<span [class]="bar.textClass + ' fw-bold text-nowrap'" style="min-width: 36px; text-align: right;">
						{{ bar.displayText }}
					</span>
					<div class="progress flex-grow-1" style="height: 6px; background-color: #e9ecef; border-radius: 3px;">
						<div
							[class]="'progress-bar ' + bar.colorClass"
							role="progressbar"
							[style.width.%]="bar.percentage"
							aria-valuemin="0"
							aria-valuemax="100"></div>
					</div>
				</div>
			}
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProgressBarCellRendererComponent implements ICellRendererAngularComp {
	renderedBars: Array<RenderedBarItem> = [];
	tooltip: string = '';

	agInit(params: ICellRendererParams & ProgressBarCellRendererParams): void {
		this.refresh(params);
	}

	refresh(params: ICellRendererParams & ProgressBarCellRendererParams): boolean {
		const configs: Array<ProgressBarItemConfig> = params.bars ?? [];

		const defaultColors = [
			{ color: 'bg-success', text: 'text-success' },
			{ color: 'bg-warning', text: 'text-warning' },
			{ color: 'bg-danger', text: 'text-danger' },
			{ color: 'bg-info', text: 'text-info' },
			{ color: 'bg-primary', text: 'text-primary' },
		];

		const tooltipParts: string[] = [];

		this.renderedBars = configs.map((cfg, index) => {
			const fallbackColor = defaultColors[index % defaultColors.length];

			let rawVal = 0;
			let percentage = 0;

			if (cfg.totalKey && params.data) {
				const denominator = Number(params.data[cfg.totalKey] ?? 0);
				const numerator = Number(params.data[cfg.key ?? ''] ?? 0);
				rawVal = numerator;
				percentage = denominator > 0 ? Math.min(100, Math.max(0, Math.round((numerator / denominator) * 100))) : 0;
			} else {
				const val = cfg.value !== undefined ? cfg.value : params.data && cfg.key ? Number(params.data[cfg.key] ?? 0) : 0;
				percentage = Math.min(100, Math.max(0, Math.round(val)));
				rawVal = val;
			}

			const labelPrefix = cfg.label ? `${cfg.label}: ` : '';
			if (cfg.totalKey) {
				tooltipParts.push(`${labelPrefix}${rawVal} (${percentage}%)`);
			} else {
				tooltipParts.push(`${labelPrefix}${percentage}%`);
			}

			const mode = cfg.displayMode ?? (cfg.totalKey ? 'value' : 'percentage');
			let displayText = `${percentage}%`;
			if (mode === 'value') {
				displayText = `${rawVal}`;
			} else if (mode === 'value_percentage') {
				displayText = `${rawVal} (${percentage}%)`;
			}

			return {
				percentage,
				displayText,
				colorClass: cfg.colorClass || fallbackColor.color,
				textClass: cfg.textClass || fallbackColor.text,
			};
		});

		if (params.summaryKey && params.data && params.data[params.summaryKey] !== undefined) {
			const label = params.totalLabel ? `${params.totalLabel}: ` : '';
			tooltipParts.push(`${label}${params.data[params.summaryKey]}`);
		}

		this.tooltip = tooltipParts.join(' • ');
		return true;
	}
}
